import React, { useState, useMemo } from 'react';
import { Search, MessageCircle, BookOpen, ArrowUpRight, ChevronDown, Clock, Mail, ExternalLink, X } from 'lucide-react';

export default function HelpSupport() {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const FAQ_ITEMS = [
    {
      id: 'better-searches',
      q: 'How do I write better search queries?',
      a: 'Be specific,  include both an industry and a location. Example: "dentists in Chicago" finds far better leads than just "dentists."',
    },
    {
      id: 'ai-score',
      q: 'What does the AI lead score mean?',
      a: 'Scores 70+ are Hot leads ready for outreach. 40-69 are Warm leads worth nurturing. Below 40 are Cool leads for your pipeline. Each lead also gets a personalized pitch angle.',
    },
    {
      id: 'export',
      q: 'How do I export leads?',
      a: 'CSV export works instantly from any lead list. For Google Sheets, connect your account in Integrations first, then the Sheets button appears.',
    },
    {
      id: 're-enrich',
      q: 'Can I re-score or re-enrich a lead?',
      a: 'Yes,  from the Company Profile page use Re-Enrich or Re-Score, or select multiple leads in History and use the batch action bar.',
    },
    {
      id: 'plan-limits',
      q: 'Where do I see my usage?',
      a: 'Your Billing page shows remaining searches, AI scores, and exports for the current billing period, plus plan comparison.',
    },
    {
      id: 'integrations',
      q: 'Which integrations are available?',
      a: 'Zapier (5,000+ apps), Google Sheets, HubSpot CRM, Slack, Mailchimp, and Notion. Visit the Integrations page to connect them.',
    },
    {
      id: 'cancel',
      q: 'How do I cancel?',
      a: 'Go to Billing → Cancel Subscription. Your plan stays active until period end, then reverts to Free. All data is preserved.',
    },
  ];

  const filteredFAQs = useMemo(() => {
    if (!searchQuery) return FAQ_ITEMS;
    const q = searchQuery.toLowerCase();
    return FAQ_ITEMS.filter(f => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q));
  }, [searchQuery]);

  const handleChatClick = () => {
    if (window.Tawk_API) {
      window.Tawk_API.maximize();
    } else {
      window.open('https://tawk.to/chat/6a43f42e8b30801d438c8d7a/default', '_blank');
    }
  };

  return (
    <div style={{
      width: '100%',
      maxWidth: '100%',
      padding: 'clamp(16px, 3vw, 32px) clamp(12px, 2vw, 24px) clamp(40px, 8vh, 80px)',
      boxSizing: 'border-box',
    }}>
      <div style={{
        maxWidth: 860,
        width: '100%',
        margin: '0 auto',
      }}>
        {/* Header */}
        <div style={{ marginBottom: 'clamp(20px, 3vw, 40px)' }}>
          <h1 style={{
            fontSize: 'clamp(18px, 4vw, 28px)',
            fontWeight: 700,
            color: 'var(--text-primary)',
            margin: '0 0 clamp(4px, 1vw, 8px)',
            lineHeight: 1.2,
          }}>
            Help Center
          </h1>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: 'clamp(12px, 2vw, 15px)',
            margin: 0,
            lineHeight: 1.6,
          }}>
            Search for answers or start a live chat,  we're here to help.
          </p>
        </div>

        {/* Search Bar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-primary)',
          borderRadius: 12,
          padding: '0 clamp(12px, 2vw, 20px)',
          height: 'clamp(44px, 7vh, 56px)',
          marginBottom: 'clamp(20px, 3vw, 32px)',
          boxShadow: 'var(--shadow-sm)',
          width: '100%',
          boxSizing: 'border-box',
        }}>
          <Search size={18} color="var(--text-tertiary)" style={{ flexShrink: 0 }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search help articles..."
            style={{
              flex: 1,
              minWidth: 0,
              background: 'none',
              border: 'none',
              color: 'var(--text-primary)',
              fontSize: 'clamp(13px, 2vw, 15px)',
              outline: 'none',
              fontFamily: 'inherit',
              padding: 0,
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-tertiary)',
                cursor: 'pointer',
                fontSize: 16,
                padding: 4,
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              }}
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Live Chat Card */}
        <div style={{
          background: 'linear-gradient(135deg, #2563EB08 0%, #2563EB15 100%)',
          border: '1px solid #2563EB33',
          borderRadius: 14,
          padding: 'clamp(14px, 3vw, 24px) clamp(14px, 3vw, 28px)',
          marginBottom: 'clamp(20px, 3vw, 36px)',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 'clamp(12px, 2vw, 20px)',
          width: '100%',
          boxSizing: 'border-box',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'clamp(10px, 2vw, 16px)',
            flex: '1 1 auto',
            minWidth: 0,
          }}>
            <div style={{
              width: 'clamp(40px, 8vw, 52px)',
              height: 'clamp(40px, 8vw, 52px)',
              borderRadius: 12,
              flexShrink: 0,
              background: '#2563EB18',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <MessageCircle size={24} color="#2563EB" style={{ width: 'clamp(18px, 4vw, 24px)', height: 'clamp(18px, 4vw, 24px)' }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 'clamp(14px, 2.5vw, 18px)',
                fontWeight: 700,
                color: 'var(--text-primary)',
                lineHeight: 1.3,
              }}>
                Live Chat
              </div>
              <div style={{
                fontSize: 'clamp(11px, 2vw, 14px)',
                color: 'var(--text-secondary)',
                marginTop: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                flexWrap: 'wrap',
              }}>
                <Clock size={13} style={{ flexShrink: 0 }} />
                <span>Typically reply in under 2 minutes</span>
              </div>
            </div>
          </div>
          <button
            onClick={handleChatClick}
            style={{
              height: 'clamp(40px, 7vh, 48px)',
              padding: '0 clamp(20px, 3vw, 28px)',
              borderRadius: 10,
              border: 'none',
              background: '#2563EB',
              color: '#fff',
              cursor: 'pointer',
              fontSize: 'clamp(13px, 2vw, 15px)',
              fontWeight: 600,
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              boxShadow: '0 4px 16px #2563EB30',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              transition: 'transform 0.15s, box-shadow 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 6px 20px #2563EB40';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 16px #2563EB30';
            }}
          >
            <MessageCircle size={16} />
            Start Chat
          </button>
        </div>

        {/* FAQ + Email Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
          gap: 'clamp(14px, 2vw, 24px)',
          width: '100%',
          boxSizing: 'border-box',
        }}
        className="help-grid"
        >
          {/* FAQ Section */}
          <div style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-primary)',
            borderRadius: 14,
            padding: 'clamp(14px, 3vw, 24px) clamp(14px, 3vw, 28px)',
            boxShadow: 'var(--shadow-sm)',
            minWidth: 0,
            boxSizing: 'border-box',
          }}>
            <h3 style={{
              fontSize: 'clamp(11px, 2vw, 13px)',
              fontWeight: 700,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              margin: '0 0 clamp(14px, 2vw, 20px)',
              display: 'flex',
              alignItems: 'center',
              gap: 7,
            }}>
              <BookOpen size={15} style={{ flexShrink: 0 }} />
              Quick Answers
            </h3>

            {filteredFAQs.length > 0 ? (
              filteredFAQs.map((faq, i) => {
                const open = expandedFAQ === faq.id;
                return (
                  <div
                    key={faq.id}
                    style={{
                      borderBottom: i < filteredFAQs.length - 1 ? '1px solid var(--border-primary)' : 'none',
                    }}
                  >
                    <button
                      onClick={() => setExpandedFAQ(open ? null : faq.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        padding: 'clamp(12px, 2vw, 16px) 0',
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: open ? 'var(--accent-primary)' : 'var(--text-primary)',
                        fontSize: 'clamp(12px, 2vw, 15px)',
                        fontWeight: 500,
                        textAlign: 'left',
                        fontFamily: 'inherit',
                        lineHeight: 1.4,
                        gap: 8,
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>{faq.q}</span>
                      <ChevronDown
                        size={16}
                        style={{
                          flexShrink: 0,
                          transition: 'transform 0.2s ease',
                          transform: open ? 'rotate(180deg)' : 'rotate(0)',
                        }}
                      />
                    </button>
                    <div
                      style={{
                        maxHeight: open ? 300 : 0,
                        overflow: 'hidden',
                        transition: 'max-height 0.3s ease',
                      }}
                    >
                      <p style={{
                        padding: '0 0 clamp(12px, 2vw, 18px)',
                        margin: 0,
                        fontSize: 'clamp(12px, 2vw, 14px)',
                        color: 'var(--text-secondary)',
                        lineHeight: 1.8,
                      }}>
                        {faq.a}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <p style={{
                fontSize: 'clamp(12px, 2vw, 14px)',
                color: 'var(--text-tertiary)',
                textAlign: 'center',
                padding: 'clamp(20px, 4vw, 32px)',
                margin: 0,
              }}>
                No results for "{searchQuery}"
              </p>
            )}
          </div>

          {/* Email Contact Card */}
          <div style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-primary)',
            borderRadius: 14,
            padding: 'clamp(14px, 3vw, 24px) clamp(14px, 3vw, 28px)',
            boxShadow: 'var(--shadow-sm)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minWidth: 0,
            boxSizing: 'border-box',
          }}>
            <div>
              <h3 style={{
                fontSize: 'clamp(11px, 2vw, 13px)',
                fontWeight: 700,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.07em',
                margin: '0 0 clamp(14px, 2vw, 20px)',
                display: 'flex',
                alignItems: 'center',
                gap: 7,
              }}>
                <Mail size={15} style={{ flexShrink: 0 }} />
                Email Us
              </h3>
              <p style={{
                fontSize: 'clamp(12px, 2vw, 15px)',
                color: 'var(--text-secondary)',
                lineHeight: 1.8,
                margin: '0 0 clamp(16px, 3vw, 24px)',
              }}>
                Prefer email? We respond to every message within a few hours, Monday to Friday.
              </p>
              <a
                href="mailto:support@ileadx.io"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                  color: 'var(--accent-primary)',
                  fontSize: 'clamp(13px, 2vw, 16px)',
                  fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'opacity 0.15s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                Contact Support <ArrowUpRight size={14} style={{ flexShrink: 0 }} />
              </a>
            </div>
            <div style={{
              marginTop: 'clamp(20px, 4vw, 32px)',
              paddingTop: 'clamp(14px, 2vw, 20px)',
              borderTop: '1px solid var(--border-primary)',
            }}>
              <a
                href="https://ileadx.io"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 'clamp(11px, 2vw, 13px)',
                  color: 'var(--text-tertiary)',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  transition: 'color 0.15s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--accent-primary)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-tertiary)'}
              >
                ileadx.io <ExternalLink size={12} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Responsive breakpoints injected as style tag */}
      <style>{`
        .help-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
          gap: clamp(14px, 2vw, 24px);
        }
        
        /* Tablet breakpoint */
        @media (max-width: 768px) {
          .help-grid {
            grid-template-columns: 1fr !important;
            gap: clamp(12px, 2vw, 16px) !important;
          }
        }

        /* Small mobile */
        @media (max-width: 480px) {
          .help-grid {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }
        }
      `}</style>
    </div>
  );
}
